import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Spinner } from 'react-bootstrap';
import { boardService } from '../services/boardService';
import Lightbox from '../../../components/common/Lightbox';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import RichTextEditorBasic from '../../../components/common/RichTextEditorBasic';
import api from '../../../utils/api';
import { fetchClub } from '../utils/clubsCache';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { stripHtmlToText } from '../board/boardDisplayUtils';
import { ClubBoardThreadHeader } from './ClubBoardThreadHeader';
import { ClubBoardReplyList } from './ClubBoardReplyList';
import { ClubBoardReplyComposer } from './ClubBoardReplyComposer';
import './ClubBoardPage.css';
import './clubBoardFieldEdition.css';
import './ClubThreadPage.css';

export default function ClubThreadPage({
  embedded = false,
  clubId: clubIdProp,
  threadId: threadIdProp,
}) {
  const { clubId: clubIdParam, threadId: threadIdParam } = useParams();
  const clubId = clubIdProp ?? clubIdParam;
  const threadId = threadIdProp ?? threadIdParam;
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const limit = 50;
  const offset = 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [replyText, setReplyText] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [replyHtml, setReplyHtml] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAdvancedReply, setShowAdvancedReply] = useState(false);
  const { currentUser } = useCurrentUser();
  const [isPresident, setIsPresident] = useState(false);

  const [isEditingThread, setIsEditingThread] = useState(false);
  const [threadTitle, setThreadTitle] = useState('');
  const [threadTags, setThreadTags] = useState('');

  const [editingPostId, setEditingPostId] = useState(null);
  const [editingBodyHtml, setEditingBodyHtml] = useState('');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await boardService.getThread(clubId, threadId, { limit, offset });
      setThread(res.thread || null);
      setPosts(res.posts || []);
      if (res.thread) {
        setThreadTitle(res.thread.title || '');
        setThreadTags((res.thread.tags || []).join(', '));
      }
    } catch {
      setError('Failed to load thread');
    } finally {
      setLoading(false);
    }
  }, [clubId, threadId, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchClub(api, clubId);
        setIsPresident(!!resp?.data?.is_president);
      } catch {
        setIsPresident(false);
      }
    })();
  }, [clubId]);

  const openLightboxForPost = useCallback((imageUrls, startIndex = 0) => {
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) return;
    const items = imageUrls.map((url) => ({ url, getUrl: () => url }));
    setLightboxItems(items);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  }, []);

  const firstPost = posts[0];
  const replies = useMemo(() => posts.slice(1), [posts]);
  const bodyText = firstPost
    ? (firstPost.body_text || stripHtmlToText(firstPost.body_html))
    : '';

  const handleUploadImage = async (file) => {
    if (!file || images.length >= 2) return;
    setUploading(true);
    try {
      const preprocess = await (async () => {
        try {
          const im = new window.Image();
          const objectUrl = URL.createObjectURL(file);
          await new Promise((res, rej) => {
            im.onload = res;
            im.onerror = rej;
            im.src = objectUrl;
          });
          const canvas = document.createElement('canvas');
          let { width, height } = im;
          const ratio = width / height;
          if (width > height) {
            if (width > 1600) {
              height = Math.round(1600 / ratio);
              width = 1600;
            }
          } else if (height > 1600) {
            width = Math.round(1600 * ratio);
            height = 1600;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(im, 0, 0, width, height);
          const mainBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.82));
          return { blob: mainBlob || file };
        } catch {
          return { blob: file };
        }
      })();

      const init = await boardService.initBoardImageUpload({
        clubId,
        threadId,
        filename: file.name,
        contentType: file.type?.startsWith('image/') ? file.type : 'image/jpeg',
      });
      await fetch(init.upload_url, {
        method: 'PUT',
        body: preprocess.blob,
        headers: { 'Content-Type': file.type?.startsWith('image/') ? file.type : 'image/jpeg' },
      });
      setImages((prev) => [...prev, init.object_key].slice(0, 2));
    } catch {
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const submitQuickReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed) return;
    setReplySaving(true);
    try {
      const escaped = trimmed
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      await boardService.createPost(clubId, threadId, { body_html: `<p>${escaped}</p>` });
      setReplyText('');
      await load();
    } catch {
      setError('Failed to post reply');
    } finally {
      setReplySaving(false);
    }
  };

  const submitAdvancedReply = async (e) => {
    e.preventDefault();
    if (!replyHtml.trim()) return;
    try {
      await boardService.createPost(clubId, threadId, { body_html: replyHtml, images });
      setReplyHtml('');
      setImages([]);
      setShowAdvancedReply(false);
      await load();
    } catch {
      setError('Failed to post reply');
    }
  };

  const canEditThread = currentUser && thread && Number(thread.created_by?.id) === Number(currentUser.id);

  const saveThreadEdits = async () => {
    if (!threadTitle.trim()) return;
    try {
      setSaving(true);
      const tags = threadTags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 4);
      await boardService.editThread(clubId, threadId, { title: threadTitle.trim(), tags });
      setIsEditingThread(false);
      await load();
    } catch {
      setError('Failed to save thread');
    } finally {
      setSaving(false);
    }
  };

  const deleteThread = async () => {
    if (!window.confirm('Delete this thread?')) return;
    try {
      let payload;
      if (isPresident && !(currentUser && Number(thread.created_by?.id) === Number(currentUser.id))) {
        const reason = window.prompt('Reason for deletion (required):');
        if (!reason?.trim()) return;
        payload = { delete_reason: reason.trim() };
      }
      await boardService.deleteThread(clubId, threadId, payload);
      navigate(`/clubs/${clubId}/board`);
    } catch {
      setError('Failed to delete thread');
    }
  };

  const canEditPost = (p) =>
    currentUser &&
    (Number(p.author?.id) === Number(currentUser.id) ||
      (p.author?.username &&
        currentUser?.username &&
        String(p.author.username).toLowerCase() === String(currentUser.username).toLowerCase()));

  const canDeletePost = (p) => canEditPost(p) || isPresident;

  const startEditPost = (p) => {
    setEditingPostId(p.id);
    setEditingBodyHtml(p.body_html || '');
  };

  const savePostEdit = async (postId) => {
    try {
      await boardService.editPost(clubId, threadId, postId, { body_html: editingBodyHtml });
      setEditingPostId(null);
      setEditingBodyHtml('');
      await load();
    } catch {
      setError('Failed to save post');
    }
  };

  const deletePost = async (postId, post) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      let payload;
      const isOwn = canEditPost(post);
      if (isPresident && !isOwn) {
        const reason = window.prompt('Reason for deletion (required):');
        if (!reason?.trim()) return;
        payload = { delete_reason: reason.trim() };
      }
      await boardService.deletePost(clubId, threadId, postId, payload);
      await load();
    } catch {
      setError('Failed to delete reply');
    }
  };

  const replyCount = replies.length;
  const replyBandLabel = `${replyCount} ${replyCount === 1 ? 'REPLY' : 'REPLIES'}`;

  return (
    <div
      className={[
        'club-board-thread-page',
        embedded ? 'club-board-thread-page--embedded' : '',
      ].filter(Boolean).join(' ')}
      data-testid="club-board-thread-page"
    >
      <div className="club-board-thread-page__toolbar">
        <YButton
          variant="secondary"
          size="sm"
          className="club-board-thread-page__back"
          onClick={() => navigate(`/clubs/${clubId}/board`)}
        >
          ‹ Back to the Board
        </YButton>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted small">Loading thread…</span>
        </div>
      ) : error && !thread ? (
        <div className="alert alert-danger py-2">{error}</div>
      ) : thread ? (
        <>
          <article className="club-board-thread-detail" data-testid="club-board-thread-detail">
            {!isEditingThread ? (
              <ClubBoardThreadHeader
                thread={thread}
                timeIso={firstPost?.created_at || thread.created_at}
                layout="detail"
              />
            ) : (
              <div className="club-board-thread-page__edit">
                <YInput value={threadTitle} onChange={(e) => setThreadTitle(e.target.value)} placeholder="Thread title" />
                <YInput
                  value={threadTags}
                  onChange={(e) => setThreadTags(e.target.value)}
                  placeholder="Tags (comma-separated)"
                />
                <div className="d-flex gap-2">
                  <YButton size="sm" onClick={saveThreadEdits} disabled={saving}>
                    Save
                  </YButton>
                  <YButton size="sm" variant="secondary" onClick={() => setIsEditingThread(false)}>
                    Cancel
                  </YButton>
                </div>
              </div>
            )}

            {(canEditThread || isPresident) && !isEditingThread && (
              <div className="club-board-thread-page__admin-actions">
                {canEditThread && (
                  <YButton variant="secondary" size="sm" onClick={() => setIsEditingThread(true)}>
                    Edit thread
                  </YButton>
                )}
                <YButton variant="secondary" size="sm" className="text-danger" onClick={deleteThread}>
                  Delete thread
                </YButton>
              </div>
            )}

            {bodyText && !editingPostId && (
              <p className="club-board-thread-detail__body">{bodyText}</p>
            )}

            {firstPost && Array.isArray(firstPost.image_urls) && firstPost.image_urls.length > 0 && (
              <div className="club-board-thread-page__images">
                {firstPost.image_urls.map((url, idx) => (
                  <button
                    key={url}
                    type="button"
                    className="club-board-thread-page__image-btn"
                    onClick={() => openLightboxForPost(firstPost.image_urls, idx)}
                  >
                    <img src={url} alt="" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </article>

          <section className="club-board-thread-replies" aria-label="Replies">
            <div className="club-board-thread-replies__band">{replyBandLabel}</div>
            <div className="club-board-thread-replies__body">
              <ClubBoardReplyList
                replies={replies}
                hideBodyForId={editingPostId}
                renderAfterReply={(p) => {
                  if (editingPostId === p.id) {
                    return (
                      <div className="club-board-thread-page__reply-edit">
                        <RichTextEditorBasic value={editingBodyHtml} onChange={setEditingBodyHtml} />
                        <div className="d-flex gap-2 mt-2">
                          <YButton size="sm" onClick={() => savePostEdit(p.id)}>
                            Save
                          </YButton>
                          <YButton
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setEditingPostId(null);
                              setEditingBodyHtml('');
                            }}
                          >
                            Cancel
                          </YButton>
                        </div>
                      </div>
                    );
                  }
                  if (!canEditPost(p) && !canDeletePost(p)) return null;
                  return (
                    <div className="club-board-thread-page__reply-actions">
                      {canEditPost(p) && (
                        <button type="button" className="club-board-thread-page__link-btn" onClick={() => startEditPost(p)}>
                          Edit
                        </button>
                      )}
                      {canDeletePost(p) && (
                        <button
                          type="button"
                          className="club-board-thread-page__link-btn club-board-thread-page__link-btn--danger"
                          onClick={() => deletePost(p.id, p)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  );
                }}
              />

              <div className="club-board-thread-replies__composer">
                {!showAdvancedReply ? (
                  <>
                    <ClubBoardReplyComposer
                      currentUser={currentUser}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          submitQuickReply();
                        }
                      }}
                      onSubmit={submitQuickReply}
                      saving={replySaving}
                    />
                    <button
                      type="button"
                      className="club-board-thread-page__advanced-toggle"
                      onClick={() => setShowAdvancedReply(true)}
                    >
                      Rich text &amp; images
                    </button>
                  </>
                ) : (
                  <Form onSubmit={submitAdvancedReply} className="club-board-thread-page__advanced-reply">
                    <RichTextEditorBasic value={replyHtml} onChange={setReplyHtml} placeholder="Write your reply" />
                    <Form.Group className="mb-2 mt-2">
                      <Form.Label className="small text-muted">Attach images (max 2)</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        disabled={images.length >= 2 || uploading}
                        onChange={(e) => handleUploadImage(e.target.files?.[0])}
                      />
                      <div className="small text-muted mt-1">{images.length}/2 selected</div>
                    </Form.Group>
                    <div className="d-flex gap-2 flex-wrap">
                      <YButton type="submit" size="sm" disabled={uploading || !replyHtml.trim()}>
                        Post reply
                      </YButton>
                      <YButton
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setShowAdvancedReply(false);
                          setReplyHtml('');
                          setImages([]);
                        }}
                      >
                        Use quick reply
                      </YButton>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </section>
        </>
      ) : (
        <p className="text-muted small mb-0">Thread not found.</p>
      )}

      {error && thread && <div className="alert alert-danger py-2 mt-3">{error}</div>}

      {lightboxOpen && (
        <Lightbox
          items={lightboxItems}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

ClubThreadPage.propTypes = {
  embedded: PropTypes.bool,
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  threadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
